<template>
    <section class="internal-page">
        <internal-page-header
            eyebrow="Informação e transparência"
            title="Atualizações e notícias"
            description="Publicações e novidades para acompanhar a atuação da PB&B e o universo da insolvência empresarial."
        />
        <div class="internal-content">
            <form class="internal-toolbar internal-toolbar--search" @submit.prevent="searchNews">
                <div class="internal-toolbar__summary">
                    <h2>Acervo de publicações</h2>
                    <p>
                        <strong>{{ dataList.count }}</strong>
                        {{
                            dataList.count === 1
                                ? 'publicação disponível'
                                : 'publicações disponíveis'
                        }}
                    </p>
                </div>
                <v-text-field
                    v-model="dataList.filter.name"
                    label="Buscar publicação"
                    placeholder="Digite um assunto ou título"
                    outlined
                    dense
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    append-icon="mdi-arrow-right"
                    aria-label="Buscar publicação por assunto ou título"
                    @click:append="searchNews"
                    @click:clear="clearSearch"
                />
            </form>
            <div
                v-if="categories.length > 1"
                class="news-filter"
                role="group"
                aria-label="Filtrar publicações por categoria"
            >
                <button
                    v-for="option in categoryOptions"
                    :key="option.id || 'all'"
                    type="button"
                    class="news-filter__option"
                    :class="{
                        'news-filter__option--active': option.id === dataList.filter.category
                    }"
                    :aria-pressed="String(option.id === dataList.filter.category)"
                    @click="selectCategory(option.id)"
                >
                    {{ option.name }}
                </button>
            </div>
            <page-state
                v-if="dataList.loading || error || !dataList.data.length"
                :loading="dataList.loading"
                :error="error"
                inline
                :title="emptyTitle"
                :description="emptyDescription"
                :icon="dataList.filter.name ? 'mdi-text-search' : 'mdi-newspaper-variant-outline'"
                @retry="load"
            />
            <div v-else class="news-grid">
                <news-card
                    v-for="item in dataList.data"
                    :key="item.id"
                    :link-url="'/categoria/noticias/' + item.id"
                    :image-url="item.cover_image_url"
                    :title="item.title"
                    :content="item.content"
                    :created-at="item.created_at"
                    :category="item.category && item.category.name"
                />
            </div>
            <v-pagination
                v-if="!dataList.loading && !error && pages > 1"
                v-model="dataList.options.page"
                :length="pages"
                :total-visible="5"
                class="mt-10"
                @input="load"
            />
        </div>
    </section>
</template>
<script>
import axios from 'axios';
import NewsCard from '@/components/NewsCard.vue';
export default {
    name: 'NewsPage',
    components: { NewsCard },
    data: () => ({
        error: false,
        categories: [],
        dataList: {
            filter: { name: '', category: null },
            data: [],
            loading: true,
            count: 0,
            options: { page: 1, size: 6 }
        }
    }),
    computed: {
        categoryOptions() {
            return [{ id: null, name: 'Todas' }, ...this.categories];
        },
        pages() {
            return Math.ceil(this.dataList.count / this.dataList.options.size);
        },
        emptyTitle() {
            return this.dataList.filter.name || this.dataList.filter.category
                ? 'Nenhuma publicação corresponde à busca'
                : 'Novas publicações em breve';
        },
        emptyDescription() {
            return this.dataList.filter.name || this.dataList.filter.category
                ? 'Revise o termo pesquisado ou escolha "Todas" para consultar todo o acervo.'
                : 'Os próximos conteúdos e atualizações da PB&B serão publicados aqui.';
        }
    },
    watch: {
        // mesma página com outra categoria na URL (ex.: menu "Atualizações" com um filtro ativo)
        '$route.query.categoria'(value) {
            const id = Number(value) || null;
            if (id === this.dataList.filter.category) return;
            this.dataList.filter.category = id;
            this.searchNews();
        }
    },
    created() {
        // ?categoria=<id> permite abrir a página já filtrada (ex.: só eventos)
        const category = Number(this.$route.query.categoria);
        if (category) this.dataList.filter.category = category;
        this.loadCategories();
        this.load();
    },
    methods: {
        async loadCategories() {
            try {
                const response = await axios.get('/api/categories', {
                    params: { limit: 50, search: JSON.stringify({ with_posts: true }) }
                });
                const content = response.data.content;
                this.categories = (Array.isArray(content) ? content : content.data || []).map(
                    (item) => ({
                        id: item.id,
                        name: item.name
                    })
                );
            } catch (error) {
                this.categories = [];
            }
        },
        selectCategory(id) {
            if (this.dataList.filter.category === id) return;
            this.dataList.filter.category = id;
            const query = { ...this.$route.query };
            if (id) query.categoria = String(id);
            else delete query.categoria;
            // mesma URL: o vue-router rejeita a navegação repetida, o que pode ser ignorado
            this.$router.replace({ query }).catch(() => null);
            this.searchNews();
        },
        searchNews() {
            this.dataList.options.page = 1;
            this.load();
        },
        clearSearch() {
            this.dataList.filter.name = '';
            this.searchNews();
        },
        async load() {
            this.dataList.loading = true;
            this.error = false;
            try {
                const response = await axios.get('/api/posts', {
                    params: {
                        page: this.dataList.options.page,
                        limit: this.dataList.options.size,
                        search: JSON.stringify({
                            title: this.dataList.filter.name || '',
                            category_id: this.dataList.filter.category || undefined
                        })
                    }
                });
                const content = response.data.content;
                this.dataList.data = content.data || [];
                this.dataList.count = Number(
                    content.paging ? content.paging.total : this.dataList.data.length
                );
            } catch (error) {
                this.error = true;
            } finally {
                this.dataList.loading = false;
            }
        }
    }
};
</script>
<style scoped>
.news-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: -8px 0 32px;
}
.news-filter__option {
    padding: 8px 18px;
    border: 1px solid #d9d5cc;
    border-radius: 999px;
    background: #fff;
    color: var(--pbb-navy);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}
.news-filter__option:hover {
    border-color: var(--pbb-copper);
}
.news-filter__option:focus-visible {
    outline: 2px solid var(--pbb-copper);
    outline-offset: 2px;
}
.news-filter__option--active {
    border-color: var(--pbb-navy);
    background: var(--pbb-navy);
    color: #fff;
}
.news-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 32px;
}
@media (max-width: 1000px) {
    .news-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
@media (max-width: 600px) {
    .news-grid {
        grid-template-columns: 1fr;
        gap: 24px;
    }
}
</style>
